/**
 * DOS518 - Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2025 Angelo Irtilaç — All rights reserved.
 *
 * ChecklistPreIntervento — Verifica obbligatoria prima di iniziare
 * l'operazione AIB. Include tutti i controlli L.A.C.E.S., comunicazioni,
 * sicurezza personale e coordinamento SOUP.
 * Al completamento genera firma digitale DOS con timestamp — documento legale.
 */
// src/components/Checklist/ChecklistPreIntervento.tsx
import React, { useState } from 'react';
import { useAppStore } from '../../store/useAppStore';

// ================================================================
// DEFINIZIONE CHECKLIST
// ================================================================

interface VoceChecklist {
  id: string;
  categoria: string;
  testo: string;
  obbligatoria: boolean;
  nota?: string;
}

const CHECKLIST: VoceChecklist[] = [
  // L — Lookout
  { id: 'L1', categoria: 'L — Lookout',
    testo: 'Lookout posizionato con visuale sul fronte fuoco',
    obbligatoria: true, nota: 'Il lookout deve avere visibilità diretta e radio funzionante' },
  { id: 'L2', categoria: 'L — Lookout',
    testo: 'Comunicazione radio verificata con il lookout',
    obbligatoria: true },
  { id: 'L3', categoria: 'L — Lookout',
    testo: 'Lookout conosce segnale di evacuazione',
    obbligatoria: true },

  // A — Awareness
  { id: 'A1', categoria: 'A — Awareness',
    testo: 'Direzione e velocità del vento verificate',
    obbligatoria: true, nota: 'Controllare FWI nel pannello Meteo' },
  { id: 'A2', categoria: 'A — Awareness',
    testo: 'Tipo di vegetazione e umidità del combustibile valutate',
    obbligatoria: true },
  { id: 'A3', categoria: 'A — Awareness',
    testo: 'Orografia del terreno analizzata (pendii, canali, creste)',
    obbligatoria: true },
  { id: 'A4', categoria: 'A — Awareness',
    testo: 'Possibili cambiamenti meteo nelle prossime 2 ore valutati',
    obbligatoria: false, nota: 'Consultare previsioni Open-Meteo nel pannello FWI' },

  // C — Communications
  { id: 'C1', categoria: 'C — Communications',
    testo: 'Canale radio SOUP confermato e funzionante',
    obbligatoria: true },
  { id: 'C2', categoria: 'C — Communications',
    testo: 'Canale radio squadre terrestri confermato',
    obbligatoria: true },
  { id: 'C3', categoria: 'C — Communications',
    testo: 'Frequenza emergenza nota a tutto il personale',
    obbligatoria: true },
  { id: 'C4', categoria: 'C — Communications',
    testo: 'Contatto con mezzi aerei (se presenti) stabilito',
    obbligatoria: false, nota: 'Solo se ci sono mezzi aerei assegnati' },

  // E — Escape routes
  { id: 'E1', categoria: 'E — Escape Routes',
    testo: 'Via di fuga primaria identificata e comunicata alle squadre',
    obbligatoria: true },
  { id: 'E2', categoria: 'E — Escape Routes',
    testo: 'Via di fuga secondaria identificata',
    obbligatoria: true },
  { id: 'E3', categoria: 'E — Escape Routes',
    testo: 'Le vie di fuga sono praticabili (no ostruzioni, larghezza sufficiente)',
    obbligatoria: true },
  { id: 'E4', categoria: 'E — Escape Routes',
    testo: 'Tempo di percorrenza via di fuga stimato e compatibile con velocità fuoco',
    obbligatoria: true },

  // S — Safety zones
  { id: 'S1', categoria: 'S — Safety Zones',
    testo: 'Zona di sicurezza primaria identificata e di dimensioni adeguate',
    obbligatoria: true, nota: 'Min 4x la altezza fiamme prevista in ogni direzione' },
  { id: 'S2', categoria: 'S — Safety Zones',
    testo: 'Zona di sicurezza raggiungibile prima del fuoco',
    obbligatoria: true },
  { id: 'S3', categoria: 'S — Safety Zones',
    testo: 'Tutto il personale conosce la zona di sicurezza',
    obbligatoria: true },

  // Sicurezza personale
  { id: 'P1', categoria: '🦺 Sicurezza Personale',
    testo: 'DPI completi (tuta ignifuga, elmetto, guanti, stivali antifiamma)',
    obbligatoria: true },
  { id: 'P2', categoria: '🦺 Sicurezza Personale',
    testo: 'Sacchetto di sopravvivenza (fire shelter) disponibile',
    obbligatoria: false, nota: 'Obbligatorio per operazioni in condizioni estreme' },
  { id: 'P3', categoria: '🦺 Sicurezza Personale',
    testo: 'Acqua personale sufficiente (min 1 litro per operatore)',
    obbligatoria: true },

  // Coordinamento COAU/SOUP
  { id: 'O1', categoria: '📋 Coordinamento COAU',
    testo: 'Scheda COAU compilata e inviata alla SOUP',
    obbligatoria: true },
  { id: 'O2', categoria: '📋 Coordinamento COAU',
    testo: 'Codice intervento ASTUTO ricevuto',
    obbligatoria: false },
  { id: 'O3', categoria: '📋 Coordinamento COAU',
    testo: 'Numero operatori e mezzi comunicati alla SOUP',
    obbligatoria: true },
  { id: 'O4', categoria: '📋 Coordinamento COAU',
    testo: 'Coordinamento con altri DOS attivi nell\'area (se presenti)',
    obbligatoria: false },
];

const CATEGORIE = [...new Set(CHECKLIST.map(v => v.categoria))];

const COLORI_CATEGORIA: Record<string, string> = {
  'L — Lookout':         '#f59e0b',
  'A — Awareness':       '#3b82f6',
  'C — Communications':  '#22c55e',
  'E — Escape Routes':   '#f97316',
  'S — Safety Zones':    '#ec4899',
  '🦺 Sicurezza Personale': '#6366f1',
  '📋 Coordinamento COAU':  '#06b6d4',
};

// ================================================================
// COMPONENTE
// ================================================================

export const ChecklistPreIntervento: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const dosAttivo = useAppStore((s) => s.dosAttivo);
  const addVoceDiario = useAppStore((s) => s.addVoceDiario);

  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [note, setNote] = useState<Record<string, string>>({});
  const [firmato, setFirmato] = useState(false);
  const [timestampFirma, setTimestampFirma] = useState('');
  const [nomeFirma, setNomeFirma] = useState(dosAttivo || '');
  const [step, setStep] = useState<'checklist' | 'firma' | 'firmato'>('checklist');
  const [categoriaAperta, setCategoriaAperta] = useState<string>(CATEGORIE[0]);

  const toggleCheck = (id: string) => {
    setChecks(p => ({ ...p, [id]: !p[id] }));
  };

  // Statistiche completamento
  const obbligatorie = CHECKLIST.filter(v => v.obbligatoria);
  const completateObbligatorie = obbligatorie.filter(v => checks[v.id]);
  const tutteObbligatorie = completateObbligatorie.length === obbligatorie.length;
  const totaleChecks = CHECKLIST.filter(v => checks[v.id]).length;
  const percentuale = Math.round(totaleChecks / CHECKLIST.length * 100);

  const handleFirma = () => {
    if (!nomeFirma.trim()) return;
    const ora = new Date().toLocaleString('it-IT');
    setTimestampFirma(ora);
    setFirmato(true);
    setStep('firmato');

    // Log nel diario
    addVoceDiario(
      'sistema', '✅',
      `Checklist pre-intervento firmata — DOS: ${nomeFirma} — ${completateObbligatorie.length}/${obbligatorie.length} voci obbligatorie`,
      { firmatario: nomeFirma, obbligatorieOK: completateObbligatorie.length, totale: CHECKLIST.length }
    );
  };

  const handleExport = () => {
    const righe = [
      '═══════════════════════════════════════════════════════',
      '     DOS518 — CHECKLIST PRE-INTERVENTO AIB',
      '═══════════════════════════════════════════════════════',
      `DOS: ${nomeFirma}`,
      `Firma: ${timestampFirma}`,
      `Completamento: ${totaleChecks}/${CHECKLIST.length} (${percentuale}%)`,
      `Obbligatorie: ${completateObbligatorie.length}/${obbligatorie.length}`,
      '───────────────────────────────────────────────────────',
      '',
      ...CATEGORIE.flatMap(cat => {
        const voci = CHECKLIST.filter(v => v.categoria === cat);
        return [
          `\n[ ${cat} ]`,
          ...voci.map(v => {
            const ok = checks[v.id] ? '✅' : '☐';
            const ob = v.obbligatoria ? ' *' : '';
            const n = note[v.id] ? `\n     Nota: ${note[v.id]}` : '';
            return `  ${ok} ${v.testo}${ob}${n}`;
          }),
        ];
      }),
      '',
      '───────────────────────────────────────────────────────',
      '* = voce obbligatoria',
      '',
      `FIRMA DIGITALE DOS: ${nomeFirma}`,
      `TIMESTAMP: ${timestampFirma}`,
      '═══════════════════════════════════════════════════════',
    ];
    const blob = new Blob([righe.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `DOS518_Checklist_${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      zIndex: 10000, overflowY: 'auto', padding: '20px 10px',
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #0f172a, #1e293b)',
        border: '2px solid #f97316',
        borderRadius: '16px', width: '100%', maxWidth: '680px',
        boxShadow: '0 20px 60px rgba(249,115,22,0.3)',
      }}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #7c2d12, #ea580c)',
          borderRadius: '14px 14px 0 0', padding: '20px 24px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h2 style={{ margin: 0, color: '#fff', fontSize: '1.3em' }}>
              ✅ Checklist Pre-Intervento AIB
            </h2>
            <small style={{ color: '#fed7aa' }}>L.A.C.E.S. + Sicurezza + Coordinamento</small>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
              width: '36px', height: '36px', borderRadius: '50%',
              cursor: 'pointer', fontSize: '1.2rem', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}
          >✕</button>
        </div>

        {/* Progress bar */}
        <div style={{ padding: '14px 24px 0' }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            color: '#94a3b8', fontSize: '0.8rem', marginBottom: '6px',
          }}>
            <span>{totaleChecks}/{CHECKLIST.length} voci completate</span>
            <span style={{ color: tutteObbligatorie ? '#22c55e' : '#f97316', fontWeight: 700 }}>
              {tutteObbligatorie ? '✅ Obbligatorie OK' : `⚠️ ${obbligatorie.length - completateObbligatorie.length} obbligatorie mancanti`}
            </span>
          </div>
          <div style={{
            height: '8px', background: '#1e293b', borderRadius: '4px', overflow: 'hidden',
          }}>
            <div style={{
              width: `${percentuale}%`, height: '100%',
              background: tutteObbligatorie
                ? 'linear-gradient(to right, #22c55e, #16a34a)'
                : 'linear-gradient(to right, #f97316, #ea580c)',
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>

        {/* STEP: CHECKLIST */}
        {step === 'checklist' && (
          <div style={{ padding: '16px 24px' }}>

            {/* Tab categorie */}
            <div style={{
              display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px',
            }}>
              {CATEGORIE.map(cat => {
                const voci = CHECKLIST.filter(v => v.categoria === cat);
                const completate = voci.filter(v => checks[v.id]).length;
                const colore = COLORI_CATEGORIA[cat] || '#64748b';
                return (
                  <button
                    key={cat}
                    onClick={() => setCategoriaAperta(cat)}
                    style={{
                      background: categoriaAperta === cat ? colore : '#1e293b',
                      color: categoriaAperta === cat ? '#fff' : '#94a3b8',
                      border: `1px solid ${categoriaAperta === cat ? 'transparent' : '#334155'}`,
                      borderRadius: '20px', padding: '4px 10px',
                      cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600,
                    }}
                  >
                    {cat.split(' — ')[0].split(' ')[0]} {completate}/{voci.length}
                  </button>
                );
              })}
            </div>

            {/* Voci della categoria selezionata */}
            {CHECKLIST.filter(v => v.categoria === categoriaAperta).map((voce) => {
              const checked = !!checks[voce.id];
              const colore = COLORI_CATEGORIA[voce.categoria] || '#64748b';
              return (
                <div
                  key={voce.id}
                  style={{
                    background: checked ? 'rgba(34,197,94,0.08)' : '#0f172a',
                    border: `1px solid ${checked ? '#22c55e' : voce.obbligatoria ? '#334155' : '#1e293b'}`,
                    borderLeft: `4px solid ${checked ? '#22c55e' : voce.obbligatoria ? colore : '#334155'}`,
                    borderRadius: '8px', padding: '12px',
                    marginBottom: '8px', cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onClick={() => toggleCheck(voce.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    {/* Checkbox */}
                    <div style={{
                      width: '22px', minWidth: '22px', height: '22px',
                      background: checked ? '#22c55e' : '#1e293b',
                      border: `2px solid ${checked ? '#22c55e' : '#475569'}`,
                      borderRadius: '4px', marginTop: '1px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.85rem', transition: 'all 0.15s',
                    }}>
                      {checked && '✓'}
                    </div>

                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{
                          color: checked ? '#86efac' : '#e2e8f0',
                          fontSize: '0.9rem',
                          textDecoration: checked ? 'line-through' : 'none',
                          opacity: checked ? 0.8 : 1,
                        }}>
                          {voce.testo}
                        </span>
                        {voce.obbligatoria && (
                          <span style={{
                            color: '#f97316', fontSize: '0.65rem',
                            background: '#431407', borderRadius: '3px',
                            padding: '1px 4px', fontWeight: 700,
                          }}>
                            OBB
                          </span>
                        )}
                      </div>
                      {voce.nota && (
                        <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '3px' }}>
                          ℹ️ {voce.nota}
                        </div>
                      )}
                      {/* Campo nota manuale */}
                      {checked && (
                        <input
                          type="text"
                          placeholder="Nota operativa (opzionale)..."
                          value={note[voce.id] || ''}
                          onChange={(e) => {
                            e.stopPropagation();
                            setNote(p => ({ ...p, [voce.id]: e.target.value }));
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            marginTop: '6px', width: '100%', boxSizing: 'border-box',
                            background: '#1e293b', color: '#94a3b8',
                            border: '1px solid #334155', borderRadius: '4px',
                            padding: '4px 8px', fontSize: '0.75rem',
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Navigazione categorie */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px' }}>
              <button
                onClick={() => {
                  const idx = CATEGORIE.indexOf(categoriaAperta);
                  if (idx > 0) setCategoriaAperta(CATEGORIE[idx - 1]);
                }}
                disabled={CATEGORIE.indexOf(categoriaAperta) === 0}
                style={{
                  background: '#1e293b', color: '#94a3b8',
                  border: '1px solid #334155', borderRadius: '6px',
                  padding: '8px 14px', cursor: 'pointer',
                  opacity: CATEGORIE.indexOf(categoriaAperta) === 0 ? 0.3 : 1,
                }}
              >
                ← Precedente
              </button>

              {CATEGORIE.indexOf(categoriaAperta) < CATEGORIE.length - 1 ? (
                <button
                  onClick={() => {
                    const idx = CATEGORIE.indexOf(categoriaAperta);
                    setCategoriaAperta(CATEGORIE[idx + 1]);
                  }}
                  style={{
                    background: '#1e3a5f', color: '#93c5fd',
                    border: '1px solid #2563eb', borderRadius: '6px',
                    padding: '8px 14px', cursor: 'pointer', fontWeight: 700,
                  }}
                >
                  Successivo →
                </button>
              ) : (
                <button
                  onClick={() => setStep('firma')}
                  style={{
                    background: tutteObbligatorie
                      ? 'linear-gradient(135deg, #166534, #22c55e)'
                      : '#334155',
                    color: tutteObbligatorie ? '#fff' : '#64748b',
                    border: 'none', borderRadius: '6px',
                    padding: '8px 20px', cursor: tutteObbligatorie ? 'pointer' : 'not-allowed',
                    fontWeight: 700,
                  }}
                >
                  {tutteObbligatorie ? '✅ Procedi alla firma →' : `⚠️ ${obbligatorie.length - completateObbligatorie.length} obbligatorie mancanti`}
                </button>
              )}
            </div>
          </div>
        )}

        {/* STEP: FIRMA */}
        {step === 'firma' && (
          <div style={{ padding: '24px' }}>
            <div style={{
              background: '#0f172a', border: '2px solid #22c55e',
              borderRadius: '12px', padding: '20px', marginBottom: '20px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '10px' }}>📋</div>
              <h3 style={{ color: '#22c55e', margin: '0 0 8px' }}>Firma Checklist</h3>
              <p style={{ color: '#94a3b8', margin: 0 }}>
                {completateObbligatorie.length}/{obbligatorie.length} voci obbligatorie · {totaleChecks}/{CHECKLIST.length} totali
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#94a3b8', fontSize: '0.85rem' }}>DOS firmatario *</label>
              <input
                type="text"
                placeholder="Nome e Cognome DOS"
                value={nomeFirma}
                onChange={(e) => setNomeFirma(e.target.value)}
                autoFocus
                style={{
                  width: '100%', boxSizing: 'border-box', marginTop: '6px',
                  background: '#1e293b', color: '#f1f5f9',
                  border: '1px solid #22c55e', borderRadius: '8px',
                  padding: '10px 14px', fontSize: '1rem',
                }}
              />
            </div>

            <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '0 0 16px' }}>
              Firmando dichiaro di aver verificato tutte le voci obbligatorie e di
              ritenere le condizioni operative conformi ai protocolli L.A.C.E.S.
              in vigore. Il documento verrà registrato nel Diario Operativo con timestamp.
            </p>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setStep('checklist')}
                style={{
                  flex: 1, background: '#1e293b', color: '#94a3b8',
                  border: '1px solid #334155', borderRadius: '8px',
                  padding: '12px', cursor: 'pointer',
                }}
              >
                ← Modifica
              </button>
              <button
                onClick={handleFirma}
                disabled={!nomeFirma.trim()}
                style={{
                  flex: 2,
                  background: nomeFirma.trim()
                    ? 'linear-gradient(135deg, #166534, #22c55e)'
                    : '#334155',
                  color: nomeFirma.trim() ? '#fff' : '#64748b',
                  border: 'none', borderRadius: '8px',
                  padding: '12px', cursor: nomeFirma.trim() ? 'pointer' : 'default',
                  fontWeight: 700, fontSize: '1rem',
                }}
              >
                ✍️ Firma e Conferma
              </button>
            </div>
          </div>
        )}

        {/* STEP: FIRMATO */}
        {step === 'firmato' && (
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <div style={{ fontSize: '4rem', marginBottom: '12px' }}>✅</div>
            <h3 style={{ color: '#22c55e', margin: '0 0 8px' }}>
              Checklist completata e firmata
            </h3>
            <p style={{ color: '#94a3b8', marginBottom: '6px' }}>
              DOS: <strong style={{ color: '#f1f5f9' }}>{nomeFirma}</strong>
            </p>
            <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '24px' }}>
              {timestampFirma}
            </p>

            <div style={{
              background: '#0f172a', border: '1px solid #22c55e',
              borderRadius: '10px', padding: '14px', marginBottom: '20px',
              textAlign: 'left',
            }}>
              <p style={{ color: '#94a3b8', margin: '0 0 6px', fontSize: '0.8rem' }}>
                Riepilogo
              </p>
              {CATEGORIE.map(cat => {
                const voci = CHECKLIST.filter(v => v.categoria === cat);
                const comp = voci.filter(v => checks[v.id]).length;
                const colore = COLORI_CATEGORIA[cat] || '#64748b';
                return (
                  <div key={cat} style={{
                    display: 'flex', justifyContent: 'space-between',
                    marginBottom: '4px', fontSize: '0.85rem',
                  }}>
                    <span style={{ color: colore }}>{cat}</span>
                    <span style={{ color: comp === voci.length ? '#22c55e' : '#f97316' }}>
                      {comp}/{voci.length}
                    </span>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleExport}
                style={{
                  flex: 1, background: '#164e63', color: '#67e8f9',
                  border: '1px solid #0891b2', borderRadius: '8px',
                  padding: '12px', cursor: 'pointer', fontWeight: 700,
                }}
              >
                ⬇️ Scarica verbale
              </button>
              <button
                onClick={onClose}
                style={{
                  flex: 1, background: '#166534', color: '#86efac',
                  border: '1px solid #22c55e', borderRadius: '8px',
                  padding: '12px', cursor: 'pointer', fontWeight: 700,
                }}
              >
                Chiudi ✓
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
